import type { LocalIfcSource } from "./local-ifc";

export type LocalSourceShell = "browser" | "tauri";

export interface SelectedLocalSource extends LocalIfcSource {
  readonly shell: LocalSourceShell;
  readonly fileReadMs: number;
}

type DialogOpen = (options: {
  multiple: false;
  directory: false;
  title: string;
  filters: { name: string; extensions: string[] }[];
}) => Promise<unknown>;

type FileRead = (path: string) => Promise<Uint8Array>;

export function isNativeLocalShell(scope: typeof globalThis = globalThis): boolean {
  return "__TAURI_INTERNALS__" in scope;
}

export function localBaseName(path: string): string {
  const cut = Math.max(path.lastIndexOf("/"), path.lastIndexOf("\\"));
  return cut === -1 ? path : path.slice(cut + 1);
}

export async function pickNativeIfcSourceWith(
  open: DialogOpen,
  readFile: FileRead,
  now: () => number = () => performance.now()
): Promise<SelectedLocalSource | null> {
  const selected = await open({
    multiple: false,
    directory: false,
    title: "Choose an IFC file",
    filters: [{ name: "IFC (STEP Physical File)", extensions: ["ifc", "IFC"] }]
  });
  if (selected === null || selected === undefined) return null;
  if (typeof selected !== "string") {
    throw new Error(`Unexpected file dialog result: expected a path string, got ${typeof selected}.`);
  }

  const startedAt = now();
  const bytes = await readFile(selected);
  const fileReadMs = Math.max(0, now() - startedAt);
  return { filename: localBaseName(selected), bytes, shell: "tauri", fileReadMs };
}

export async function pickNativeIfcSource(): Promise<SelectedLocalSource | null> {
  const [{ open }, { readFile }] = await Promise.all([
    import("@tauri-apps/plugin-dialog"),
    import("@tauri-apps/plugin-fs")
  ]);
  return pickNativeIfcSourceWith(open, readFile);
}

export async function sourceFromBrowserFile(file: File): Promise<SelectedLocalSource> {
  const startedAt = performance.now();
  const bytes = new Uint8Array(await file.arrayBuffer());
  return {
    filename: file.name,
    bytes,
    shell: "browser",
    fileReadMs: Math.max(0, performance.now() - startedAt)
  };
}
